// Sign Off Anyway — the pod's failed checks, shown before an allowed sign-off.
//
// A failed check holds the Done everywhere (interpod_quality refuses the
// sign-off). At a work centre flagged to allow it — the flowline stations and
// EOL QC, where a pod missing its door and jamb still has to leave the line —
// the server lets the sign-off through, and this sheet is the look first:
// this step's failed checks, then the ones earlier steps left on the pod,
// and one red button that signs off with all of them still failed.

import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { usePodFailedChecks } from '@/queries';
import type { CheckVM, FailedCheckVM, PodVM } from '@/api/types';

export function SignOffAnywaySheet({
  open,
  pod,
  ownFailed,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pod: PodVM;
  /** This step's own failed checks, from the list already on screen. */
  ownFailed: CheckVM[];
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const podFailed = usePodFailedChecks(pod.moId, open);
  // Earlier steps' failed checks: everything on the pod that is not this
  // work order's. Those are drawn from the live list above, which is fresher.
  const earlier: FailedCheckVM[] = (podFailed.data ?? []).filter((c) => c.woId !== pod.woId);
  const rows: { key: string; title: string; where: string }[] = [
    ...ownFailed.map((c) => ({ key: `own-${c.id}`, title: c.title, where: t.signOffAnywayThisStepT })),
    ...earlier.map((c) => ({ key: `pod-${c.id}`, title: c.title, where: c.workcentreName })),
  ];

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.signOffAnywayTitleT}
      footer={
        <Button variant="danger" size="md" pending={pending} onClick={onConfirm} style={{ minHeight: 48, borderRadius: 13 }}>
          {t.signOffAnywayButtonT}
        </Button>
      }
    >
      <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 12px' }}>
        {[pod.mo, pod.proj, pod.type].filter(Boolean).join(' · ')}
      </p>
      <p style={{ font: '500 13px Manrope,sans-serif', color: 'var(--ink)', margin: '0 0 12px' }}>
        {t.signOffAnywayBodyT}
      </p>
      {podFailed.isLoading && !ownFailed.length ? (
        <Skeleton height={48} />
      ) : rows.length === 0 ? (
        <EmptyState>{t.signOffAnywayNoneT}</EmptyState>
      ) : (
        <ul data-testid="signoff-anyway-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {rows.map((r) => (
            <li
              key={r.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 11,
                border: '1.5px solid var(--danger)',
                color: 'var(--ink)',
                font: '600 13px Manrope,sans-serif',
              }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</span>
              <span style={{ color: 'var(--danger)', whiteSpace: 'nowrap', font: '500 12px Manrope,sans-serif' }}>{r.where}</span>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
